import React from 'react';
import cn from 'classnames';

import { NOOP } from '../../constants';
import { MultiTree } from '../../utils/topology';
import { ComparisonMap as CMap } from '../../utils/dataStructures';
import { areSetsEqual } from '../../utils/comparators';
import { path as d3Path } from 'd3-path';

import styles from '../../css/components/topology.module.scss';

export const NODE_X_DIM = 36;
export const NODE_Y_DIM = 36;
export const CIRCLE_NODE_X_DIM = 40;
export const CIRCLE_NODE_Y_DIM = 40;
export const CIRCLE_NODE_RADIUS = 20;
const ARROW_X_DIM = 5;
const ARROW_Y_DIM = 5;
const Y_PADDING = 40;
const ROW_HEIGHT = NODE_Y_DIM + Y_PADDING;
const TREE_PADDING = 50;
const CONTROL_POINT_OFFSET = 50;
const DEPTH_LEVEL_X_SEPARATION = 240;
const X_CORRIDOR_WIDTH = DEPTH_LEVEL_X_SEPARATION/1.5;

// TODO: try using a loader...
const tapBox = `
<g id="tap" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="square" fill="#4FAAD7" fill-rule="nonzero" x="0" y="0" width="36" height="36"></rect>
  <g id="Group-4" transform="translate(2.000000, 7.000000)" fill="#FFFFFF">
      <polygon id="Fill-1" points="14.921 11.7691 18.983 7.9101 20.463 9.1851 14.921 14.5931 11.314 11.0661 12.719 9.7491"></polygon>
      <polygon id="Fill-2" points="10.2044 0 6.0764 7.536 0.0004 7.536 0.0004 10.036 7.5564 10.036 11.6854 2.5 20.0914 2.5 24.2214 10.036 31.7774 10.036 31.7774 7.536 25.7014 7.536 21.5724 0"></polygon>
      <polygon id="Fill-3" points="24.3909746 12 20.2329987 19.5089677 11.7670013 19.5089677 7.61003241 12 0 12 0 14.4910323 6.11863927 14.4910323 10.2756081 22 21.7233848 22 25.8813607 14.4910323 32 14.4910323 32 12"></polygon>
  </g>
</g>
`;
const tapPausedBox = `
<g id="tap-paused" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="Rectangle" fill="#2D3F56" fill-rule="nonzero" x="0" y="0" width="36" height="36"></rect>
  <rect id="Rectangle" fill="#FFFFFF" x="15" y="15" width="2" height="6"></rect>
  <rect id="Rectangle-Copy-2" fill="#FFFFFF" x="19" y="15" width="2" height="6"></rect>
  <g id="Group-4" transform="translate(2.000000, 7.000000)" fill="#FFFFFF">
      <polygon id="Fill-2" points="10.2044 0 6.0764 7.536 0.0004 7.536 0.0004 10.036 7.5564 10.036 11.6854 2.5 20.0914 2.5 24.2214 10.036 31.7774 10.036 31.7774 7.536 25.7014 7.536 21.5724 0"></polygon>
      <polygon id="Fill-3" points="24.3909746 12 20.2329987 19.5089677 11.7670013 19.5089677 7.61003241 12 0 12 0 14.4910323 6.11863927 14.4910323 10.2756081 22 21.7233848 22 25.8813607 14.4910323 32 14.4910323 32 12"></polygon>
  </g>
</g>
`;
const tapStoppedBox = `
<g id="tap-stopped" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="square" fill="#A22330" fill-rule="nonzero" x="0" y="0" width="36" height="36"></rect>
  <rect id="Rectangle" fill="#FFFFFF" x="14" y="17" width="8" height="2"></rect>
  <g id="Group-4" transform="translate(2.000000, 7.000000)" fill="#FFFFFF">
      <polygon id="Fill-2" points="10.2044 0 6.0764 7.536 0.0004 7.536 0.0004 10.036 7.5564 10.036 11.6854 2.5 20.0914 2.5 24.2214 10.036 31.7774 10.036 31.7774 7.536 25.7014 7.536 21.5724 0"></polygon>
      <polygon id="Fill-3" points="24.3909746 12 20.2329987 19.5089677 11.7670013 19.5089677 7.61003241 12 2.13162821e-14 12 2.13162821e-14 14.4910323 6.11863927 14.4910323 10.2756081 22 21.7233848 22 25.8813607 14.4910323 32 14.4910323 32 12"></polygon>
  </g>
</g>
`;
const tapInferredBox = `
<g id="tap-inferred" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="square" fill="#CFD5DC" fill-rule="nonzero" x="0" y="0" width="36" height="36"></rect>
  <polygon id="Fill-1" fill="#FFFFFF" points="16.921 18.7691 20.983 14.9101 22.463 16.1851 16.921 21.5931 13.314 18.0661 14.719 16.7491"></polygon>
  <polygon id="Fill-2" fill="#FFFFFF" points="12.2044 7 8.0764 14.536 2.0004 14.536 2.0004 17.036 9.5564 17.036 13.6854 9.5 22.0914 9.5 26.2214 17.036 33.7774 17.036 33.7774 14.536 27.7014 14.536 23.5724 7"></polygon>
  <polygon id="Fill-3" fill="#FFFFFF" points="26.3909746 19 22.2329987 26.5089677 13.7670013 26.5089677 9.61003241 19 2 19 2 21.4910323 8.11863927 21.4910323 12.2756081 29 23.7233848 29 27.8813607 21.4910323 34 21.4910323 34 19"></polygon>
</g>
`;
const flowBox = `
<g id="flow" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <g id="Group-8-Copy-70">
        <rect id="Rectangle-Copy-14" fill="#287CA5" fill-rule="nonzero" x="0" y="0" width="40" height="40" rx="20"></rect>
        <path d="M30.833,22.5 C32.214,22.5 33.333,21.381 33.333,20 C33.333,18.62 32.214,17.5 30.833,17.5 C29.809,17.5 28.93,18.118 28.544,19 L20.396,19 C20.698,16.264 22.309,15 25.509,15 L28.844,15 C29.301,15.604 30.018,16 30.833,16 C32.214,16 33.333,14.881 33.333,13.5 C33.333,12.12 32.214,11 30.833,11 C29.623,11 28.615,11.859 28.383,13 L25.509,13 L25.226,13.003 C21.099,13.093 18.753,15.186 18.404,19 L11.362,19 C10.96,17.962 9.958,17.223 8.777,17.223 C7.243,17.223 6,18.466 6,20 C6,21.534 7.243,22.778 8.777,22.778 C9.958,22.778 10.96,22.039 11.362,21 L18.381,21 C18.733,24.912 21.2,27 25.509,27 L28.383,27 C28.615,28.142 29.623,29 30.833,29 C32.214,29 33.333,27.881 33.333,26.5 C33.333,25.12 32.214,24 30.833,24 C30.018,24 29.301,24.396 28.844,25 L25.509,25 L25.251,24.998 C22.221,24.932 20.688,23.663 20.395,21 L28.544,21 C28.93,21.883 29.809,22.5 30.833,22.5" id="Fill-1" fill="#FFFFFF"></path>
    </g>
</g>
<metadata>
   <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
      <rdf:Description xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/">
         <dc:format>image/svg+xml</dc:format>
         <dc:rights>
            <rdf:Alt>
               <rdf:li xml:lang="x-default">Copyright (c) 2021, Autonomic LLC - All rights reserved</rdf:li>
            </rdf:Alt>
         </dc:rights>
         <xmpRights:Marked>True</xmpRights:Marked>
         <xmpRights:WebStatement>www.autonomic.ai</xmpRights:WebStatement>
      </rdf:Description>
   </rdf:RDF>
</metadata>
`;
const flowInferredBox = `
<g id="flow-inferred" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <g id="Group-8-Copy-70">
        <rect id="Rectangle-Copy-14" fill="#CFD5DC" fill-rule="nonzero" x="0" y="0" width="40" height="40" rx="20"></rect>
        <path d="M30.833,22.5 C32.214,22.5 33.333,21.381 33.333,20 C33.333,18.62 32.214,17.5 30.833,17.5 C29.809,17.5 28.93,18.118 28.544,19 L20.396,19 C20.698,16.264 22.309,15 25.509,15 L28.844,15 C29.301,15.604 30.018,16 30.833,16 C32.214,16 33.333,14.881 33.333,13.5 C33.333,12.12 32.214,11 30.833,11 C29.623,11 28.615,11.859 28.383,13 L25.509,13 L25.226,13.003 C21.099,13.093 18.753,15.186 18.404,19 L11.362,19 C10.96,17.962 9.958,17.223 8.777,17.223 C7.243,17.223 6,18.466 6,20 C6,21.534 7.243,22.778 8.777,22.778 C9.958,22.778 10.96,22.039 11.362,21 L18.381,21 C18.733,24.912 21.2,27 25.509,27 L28.383,27 C28.615,28.142 29.623,29 30.833,29 C32.214,29 33.333,27.881 33.333,26.5 C33.333,25.12 32.214,24 30.833,24 C30.018,24 29.301,24.396 28.844,25 L25.509,25 L25.251,24.998 C22.221,24.932 20.688,23.663 20.395,21 L28.544,21 C28.93,21.883 29.809,22.5 30.833,22.5" id="Fill-1" fill="#FFFFFF"></path>
    </g>
</g>
<metadata>
   <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
      <rdf:Description xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/">
         <dc:format>image/svg+xml</dc:format>
         <dc:rights>
            <rdf:Alt>
               <rdf:li xml:lang="x-default">Copyright (c) 2021, Autonomic LLC - All rights reserved</rdf:li>
            </rdf:Alt>
         </dc:rights>
         <xmpRights:Marked>True</xmpRights:Marked>
         <xmpRights:WebStatement>www.autonomic.ai</xmpRights:WebStatement>
      </rdf:Description>
   </rdf:RDF>
</metadata>
`;

const processorBox = `
<g id="processor" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="round-box" fill="#4FAAD7" fill-rule="nonzero" x="0" y="0" width="36" height="36" rx="8"></rect>
  <polygon id="Check" fill="#FFFFFF" fill-rule="nonzero" points="17.0126747 18.7958766 21.1037573 14.704794 22.5549497 16.2118169 17.0126747 21.6197529 13.5549497 18.2389166 14.8111236 16.7756298"></polygon>
  <path d="M33.883412,17.826472 L29.132732,13.074472 L24.382052,17.826472 L27.957932,17.826472 C27.911732,23.254312 23.485772,27.656512 18.047372,27.656512 C15.278012,27.656512 12.773972,26.512072 10.973492,24.674632 L8.637092,25.828312 C10.916732,28.477552 14.285372,30.165832 18.047372,30.165832 C24.869132,30.165832 30.419732,24.637672 30.465932,17.826472 L33.883412,17.826472 Z" id="Fill-1" fill="#FFFFFF"></path>
  <path d="M8.140904,17.826472 C8.140904,17.798752 8.134304,17.771032 8.134304,17.743312 C8.134304,12.275872 12.581384,7.828792 18.047504,7.828792 C20.818184,7.828792 23.322224,8.973232 25.122704,10.810672 L27.459104,9.658312 C25.179464,7.007752 21.808184,5.319472 18.047504,5.319472 C11.198024,5.319472 5.626304,10.892512 5.626304,17.743312 C5.626304,17.771032 5.630264,17.798752 5.630264,17.826472 L2.000264,17.826472 L6.750944,22.577152 L11.501624,17.826472 L8.140904,17.826472 Z" id="Fill-4" fill="#FFFFFF"></path>
</g>
`;
const processorInferredBox = `
<g id="processor-inferred" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="round-box" fill="#CFD5DC" fill-rule="nonzero" x="0" y="0" width="36" height="36" rx="8"></rect>
  <polygon id="Check" fill="#FFFFFF" fill-rule="nonzero" points="17.0126747 18.7958766 21.1037573 14.704794 22.5549497 16.2118169 17.0126747 21.6197529 13.5549497 18.2389166 14.8111236 16.7756298"></polygon>
  <path d="M33.883412,17.826472 L29.132732,13.074472 L24.382052,17.826472 L27.957932,17.826472 C27.911732,23.254312 23.485772,27.656512 18.047372,27.656512 C15.278012,27.656512 12.773972,26.512072 10.973492,24.674632 L8.637092,25.828312 C10.916732,28.477552 14.285372,30.165832 18.047372,30.165832 C24.869132,30.165832 30.419732,24.637672 30.465932,17.826472 L33.883412,17.826472 Z" id="Fill-1" fill="#FFFFFF"></path>
  <path d="M8.140904,17.826472 C8.140904,17.798752 8.134304,17.771032 8.134304,17.743312 C8.134304,12.275872 12.581384,7.828792 18.047504,7.828792 C20.818184,7.828792 23.322224,8.973232 25.122704,10.810672 L27.459104,9.658312 C25.179464,7.007752 21.808184,5.319472 18.047504,5.319472 C11.198024,5.319472 5.626304,10.892512 5.626304,17.743312 C5.626304,17.771032 5.630264,17.798752 5.630264,17.826472 L2.000264,17.826472 L6.750944,22.577152 L11.501624,17.826472 L8.140904,17.826472 Z" id="Fill-4" fill="#FFFFFF"></path>
</g>
`;
const processorPausedBox = `
<g id="processor-paused" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="rounded-box" fill="#2D3F56" fill-rule="nonzero" x="0" y="0" width="36" height="36" rx="8"></rect>
  <rect id="Rectangle" fill="#FFFFFF" x="15" y="15" width="2" height="5"></rect>
  <rect id="Rectangle-Copy-2" fill="#FFFFFF" x="19" y="15" width="2" height="5"></rect>
  <path d="M33.883412,17.826472 L29.132732,13.074472 L24.382052,17.826472 L27.957932,17.826472 C27.911732,23.254312 23.485772,27.656512 18.047372,27.656512 C15.278012,27.656512 12.773972,26.512072 10.973492,24.674632 L8.637092,25.828312 C10.916732,28.477552 14.285372,30.165832 18.047372,30.165832 C24.869132,30.165832 30.419732,24.637672 30.465932,17.826472 L33.883412,17.826472 Z" id="Fill-1" fill="#FFFFFF"></path>
  <path d="M8.140904,17.826472 C8.140904,17.798752 8.134304,17.771032 8.134304,17.743312 C8.134304,12.275872 12.581384,7.828792 18.047504,7.828792 C20.818184,7.828792 23.322224,8.973232 25.122704,10.810672 L27.459104,9.658312 C25.179464,7.007752 21.808184,5.319472 18.047504,5.319472 C11.198024,5.319472 5.626304,10.892512 5.626304,17.743312 C5.626304,17.771032 5.630264,17.798752 5.630264,17.826472 L2.000264,17.826472 L6.750944,22.577152 L11.501624,17.826472 L8.140904,17.826472 Z" id="Fill-4" fill="#FFFFFF"></path>
</g>
`;
const processorStoppedBox = `
<g id="processor-stopped" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
  <rect id="rounded-box" fill="#A22330" fill-rule="nonzero" x="0" y="0" width="36" height="36" rx="8"></rect>
  <rect id="Rectangle" fill="#FFFFFF" x="14" y="17" width="8" height="2"></rect>
  <path d="M33.883412,17.826472 L29.132732,13.074472 L24.382052,17.826472 L27.957932,17.826472 C27.911732,23.254312 23.485772,27.656512 18.047372,27.656512 C15.278012,27.656512 12.773972,26.512072 10.973492,24.674632 L8.637092,25.828312 C10.916732,28.477552 14.285372,30.165832 18.047372,30.165832 C24.869132,30.165832 30.419732,24.637672 30.465932,17.826472 L33.883412,17.826472 Z" id="Fill-1" fill="#FFFFFF"></path>
  <path d="M8.140904,17.826472 C8.140904,17.798752 8.134304,17.771032 8.134304,17.743312 C8.134304,12.275872 12.581384,7.828792 18.047504,7.828792 C20.818184,7.828792 23.322224,8.973232 25.122704,10.810672 L27.459104,9.658312 C25.179464,7.007752 21.808184,5.319472 18.047504,5.319472 C11.198024,5.319472 5.626304,10.892512 5.626304,17.743312 C5.626304,17.771032 5.630264,17.798752 5.630264,17.826472 L2.000264,17.826472 L6.750944,22.577152 L11.501624,17.826472 L8.140904,17.826472 Z" id="Fill-4" fill="#FFFFFF"></path>
</g>
`;

const forkBox = `
<g id="fork" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <polygon id="Path-2" fill="#4FAAD7" points="0 4 18 0 36 4 36 32 18 36 0 32"></polygon>
    <polygon id="Fill-2" fill="#FFFFFF" points="13.5921 7 9.2381 14.56 3.0001 14.56 3.0001 17.06 10.6831 17.06 15.0371 9.5 33 9.5 33 7"></polygon>
    <polygon id="Fill-3" fill="#FFFFFF" points="27.0033569 16.5 33 16.5 33 12.5 25.0037842 12.5"></polygon>
    <polygon id="Fill-4" fill="#FFFFFF" points="27.0175781 19.5 25.0037842 23.5 33 23.4721599 33 19.4418945"></polygon>
    <polygon id="Fill-5" fill="#FFFFFF" points="3 19 3 21.4840954 9.28177613 21.4840954 13.666331 29 33 29 33 26.5149105 15.1224785 26.5149105 10.7369166 19"></polygon>
    <polygon id="Fill-1" fill="#FFFFFF" points="18.9208 18.8627 22.9828 15.0037 24.4628 16.2787 18.9208 21.6867 15.3138 18.1597 16.7188 16.8427"></polygon>
</g>
`;
const forkPausedBox = `
<g id="fork-paused" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <polygon id="Path-2" fill="#2D3F56" points="0 4 18 0 36 4 36 32 18 36 0 32"></polygon>
    <g id="Group-6" transform="translate(3.000000, 7.000000)" fill="#FFFFFF">
        <path d="M30,0 L30,2.5 L12.0371,2.5 L7.6831,10.06 L0.0001,10.06 L0.0001,7.56 L6.2381,7.56 L10.5921,0 L30,0 Z M30,5.5 L30,9.5 L24.0033569,9.5 L22.0037842,5.5 L30,5.5 Z" id="Fill-6"></path>
        <polygon id="Fill-4" points="24.0175781 12.5 22.0037842 16.5 30 16.4721599 30 12.4418945"></polygon>
        <polygon id="Fill-5" points="0 12 0 14.4840954 6.28177613 14.4840954 10.666331 22 30 22 30 19.5149105 12.1224785 19.5149105 7.73691664 12"></polygon>
    </g>
    <rect id="Rectangle" fill="#FFFFFF" x="15" y="15" width="8" height="2"></rect>
    <rect id="Rectangle" fill="#FFFFFF" x="15" y="19" width="8" height="2"></rect>
</g>
`;
const forkStoppedBox = `
<g id="fork-stopped" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <polygon id="Path-2" fill="#A22330" points="0 4 18 0 36 4 36 32 18 36 0 32"></polygon>
    <g id="Group-6" transform="translate(3.000000, 7.000000)" fill="#FFFFFF">
        <path d="M30,0 L30,2.5 L12.0371,2.5 L7.6831,10.06 L0.0001,10.06 L0.0001,7.56 L6.2381,7.56 L10.5921,0 L30,0 Z M30,5.5 L30,9.5 L24.0033569,9.5 L22.0037842,5.5 L30,5.5 Z" id="Fill-6"></path>
        <polygon id="Fill-4" points="24.0175781 12.5 22.0037842 16.5 30 16.4721599 30 12.4418945"></polygon>
        <polygon id="Fill-5" points="0 12 0 14.4840954 6.28177613 14.4840954 10.666331 22 30 22 30 19.5149105 12.1224785 19.5149105 7.73691664 12"></polygon>
    </g>
    <rect id="Rectangle" fill="#FFFFFF" x="15" y="17" width="8" height="2"></rect>
</g>
`;
const forkInferredBox = `
<g id="fork-inferred" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <polygon id="Path-2" fill="#CFD5DC" points="0 4 18 0 36 4 36 32 18 36 0 32"></polygon>
    <polygon id="Fill-2" fill="#FFFFFF" points="13.5921 7 9.2381 14.56 3.0001 14.56 3.0001 17.06 10.6831 17.06 15.0371 9.5 33 9.5 33 7"></polygon>
    <polygon id="Fill-3" fill="#FFFFFF" points="27.0033569 16.5 33 16.5 33 12.5 25.0037842 12.5"></polygon>
    <polygon id="Fill-4" fill="#FFFFFF" points="27.0175781 19.5 25.0037842 23.5 33 23.4721599 33 19.4418945"></polygon>
    <polygon id="Fill-5" fill="#FFFFFF" points="3 19 3 21.4840954 9.28177613 21.4840954 13.666331 29 33 29 33 26.5149105 15.1224785 26.5149105 10.7369166 19"></polygon>
    <polygon id="Fill-1" fill="#FFFFFF" points="18.9208 18.8627 22.9828 15.0037 24.4628 16.2787 18.9208 21.6867 15.3138 18.1597 16.7188 16.8427"></polygon>
</g>
`;
const routeBox = `
<g id="route" clip-path="url(#clip0_248_76)">
  <path d="M29.25 0H0V29.25L6.75 36H36V6.75L29.25 0Z" fill="#4FAAD7"/>
  <g clip-path="url(#clip1_248_76)">
    <path d="M7.875 10.6875H23.9625L9.21375 24.3787L11.1263 26.4375L25.9088 12.7238V29.1938H28.7213V7.875H7.875V10.6875Z" fill="white"/>
  </g>
</g>
`;
const routeInferredBox = `
<g id="route-inferred" clip-path="url(#clip0_248_76)">
  <path d="M29.25 0H0V29.25L6.75 36H36V6.75L29.25 0Z" fill="#CFD5DC"/>
  <g clip-path="url(#clip1_248_76)">
    <path d="M7.875 10.6875H23.9625L9.21375 24.3787L11.1263 26.4375L25.9088 12.7238V29.1938H28.7213V7.875H7.875V10.6875Z" fill="white"/>
  </g>
</g>
`;

export function ArrowHeadMarker({ id }) {
  return (
    <marker id={id} viewBox="-0 -5 10 10" refX="0" refY="0" orient="auto" markerWidth={ARROW_X_DIM} markerHeight={ARROW_Y_DIM} xoverflow="visible" strokeWidth="0">
      <path d="M 0,-5 L 10 ,0 L 0,5"></path>
    </marker>
  );
}

export function TopologySvg({ children, className, viewBox, onClick=NOOP }) {
  return (
    <svg className={cn(styles.container, className)} viewBox={viewBox} onClick={onClick} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
      <defs>
        <ArrowHeadMarker id="arrowhead" />
      </defs>
      <defs dangerouslySetInnerHTML={{ __html: tapBox }} />
      <defs dangerouslySetInnerHTML={{ __html: tapPausedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: tapStoppedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: tapInferredBox }} />
      <defs dangerouslySetInnerHTML={{ __html: flowBox }} />
      <defs dangerouslySetInnerHTML={{ __html: flowInferredBox }} />
      <defs dangerouslySetInnerHTML={{ __html: processorBox }} />
      <defs dangerouslySetInnerHTML={{ __html: processorInferredBox }} />
      <defs dangerouslySetInnerHTML={{ __html: processorPausedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: processorStoppedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: forkBox }} />
      <defs dangerouslySetInnerHTML={{ __html: forkPausedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: forkStoppedBox }} />
      <defs dangerouslySetInnerHTML={{ __html: forkInferredBox }} />
      <defs dangerouslySetInnerHTML={{ __html: routeBox }} />
      <defs dangerouslySetInnerHTML={{ __html: routeInferredBox }} />
      { children }
    </svg>
  );
}


function adjustPositionOverlap(positionedNodes, node, adjusted=undefined) {
  if(adjusted === false) {
    return node.y;
  }
  // check if this node overlaps with positioned node
  for( let positionedNode of positionedNodes) {
    const overlapX = positionedNode.x === node.x;
    const overlapY = positionedNode.y === node.y || (positionedNode.y <= (node.y+CIRCLE_NODE_Y_DIM) && positionedNode.y >= (node.y-CIRCLE_NODE_Y_DIM));
    if(overlapX && overlapY) {
      // if there is an overlap then add ROW_HEIGHT to node.y
      node.y += ROW_HEIGHT;
      adjustPositionOverlap(positionedNodes, node, true);
    }
  }
  return node.y;
}

// NOTE: this annotates original nodes
export function positionTreesNodes(trees, xOrigin=0, yOrigin=0) {
  let treeYOrigin = yOrigin;
  for (let tree of trees.values()) {
    const positionedNodes = new Map();
    const treePositionedNodes = [];
    for (let [depth, nodes] of tree.sortedNodesByMaxDepth) {
      const positioned = [];

      // The node's starting y position is the average of its parent's starting
      // y position (yTop).
      const parentsYOffset = new CMap([], areSetsEqual);
      for (let node of nodes.values()) {
        const nodeParents = node.sortedInput ? node.sortedInput : node.input;
        const parents = new Set(nodeParents.keys());
        let avgYTop = 0;
        for (let parent of nodeParents.values()) {
          const positionedParent = positionedNodes.get(parent.maxDepth).get(parent.aui);
          avgYTop += positionedParent.yTop;
        }
        avgYTop = parents.size ? avgYTop/parents.size : avgYTop;
        parentsYOffset.set(parents, avgYTop);
      }

      const yOffset = new CMap([], areSetsEqual); // per parents
      for (let node of nodes.values()) {
        const nodeParents = node.sortedInput ? node.sortedInput : node.input;

        // position with the parents
        const parents = new Set(nodeParents.keys());

        // distance of parents node's bounding box from the top of the tree
        const parentYTop = nodeParents.size ? parentsYOffset.get(parents) : 0;

        // incremented offset grouped by parents
        const nodeYOffset = yOffset.get(parents) || 0;

        // actual rendered expanded height
        const nodeExpandedHeight = (node.expandedHeight*ROW_HEIGHT - NODE_Y_DIM) / 2;

        // TODO: skip x padding if depth === 0
        node.x = depth*(DEPTH_LEVEL_X_SEPARATION + NODE_X_DIM);

        node.yTop = nodeYOffset + parentYTop;
        // move yTop up so multiple parents can share this node's space
        if (parents.size > 1) {
          node.yTop -= nodeExpandedHeight / parents.size;
        }

        node.y = node.yTop + nodeExpandedHeight;

        // Position relative to tree's origin
        node.x += xOrigin;
        node.y += treeYOrigin;

        // check and adjust for any node overlaps
        node.y = adjustPositionOverlap(treePositionedNodes, node);

        yOffset.set(parents, nodeYOffset + node.expandedHeight*ROW_HEIGHT);
        positioned.push(node);
        treePositionedNodes.push(node);
      }

      positionedNodes.set(depth, new Map(positioned.map(n => ([n.aui, n]))));
    }

    treeYOrigin += tree.expandedHeight * (NODE_Y_DIM + Y_PADDING) + TREE_PADDING;

    // TODO: remove yTop from nodes since it is relative and not useful outside this function
  }
}


export function renderEdge(parentNode, childNode, svg) {
  const path = d3Path();
  // Account for circle nodes being a bit larger
  const [
    parentNodeDimX,
    parentNodeDimY
  ] = parentNode.type === 'flow' ? [CIRCLE_NODE_X_DIM, CIRCLE_NODE_Y_DIM] : [NODE_X_DIM, NODE_Y_DIM];
  const childNodeDimY = childNode.type === 'flow' ? CIRCLE_NODE_Y_DIM : NODE_Y_DIM;
  const [startX, startY] = [parentNode.x + parentNodeDimX, parentNode.y + parentNodeDimY / 2];
  const [endX, endY] = [childNode.x - 2*ARROW_X_DIM, childNode.y + childNodeDimY / 2];
  path.moveTo(startX, startY);
  path.bezierCurveTo(endX - CONTROL_POINT_OFFSET, startY, startX + CONTROL_POINT_OFFSET, endY, endX, endY);
  svg
    .append('path')
    .attr('d', path.toString())
    .attr('data-from', parentNode.aui)
    .attr('data-to', childNode.aui)
    .attr('class', styles.edge);
}


export function renderLongEdge(parentNode, childNode, xOffset, svg) {
  const path = d3Path();
  // Account for circle nodes being a bit larger
  const [
    parentNodeDimX,
    parentNodeDimY
  ] = parentNode.type === 'flow' ? [CIRCLE_NODE_X_DIM, CIRCLE_NODE_Y_DIM] : [NODE_X_DIM, NODE_Y_DIM];
  const childNodeDimY = childNode.type === 'flow' ? CIRCLE_NODE_Y_DIM : NODE_Y_DIM;
  const [startX, startY] = [parentNode.x + parentNodeDimX, parentNode.y + parentNodeDimY / 2];
  const [endX, endY] = [childNode.x - 2*ARROW_X_DIM, childNode.y + childNodeDimY / 2];
  path.moveTo(startX, startY);

  const midX = endX - DEPTH_LEVEL_X_SEPARATION/2 + xOffset;
  path.arcTo(midX, startY, midX, startY + (endY - startY)/2, ROW_HEIGHT/2);
  path.arcTo(midX, endY, endX, endY, ROW_HEIGHT/2);
  path.lineTo(endX, endY);

  svg
    .append('path')
    .attr('d', path.toString())
    .attr('data-from', parentNode.aui)
    .attr('data-to', childNode.aui)
    .attr('class', styles.edge);
}


function buildCorridors(edges) {
  // Build corridors (columns where the edges traverse)
  const corridors = new Map();
  for (let edge of edges) {
    const { child } = edge;
    const corridorIdx = child.onlyMaxDepth;
    if (!corridors.has(corridorIdx)) {
      // using a set will allow different children with the same parent to
      // have overlapping paths
      corridors.set(corridorIdx, {
        children: new Set(),
        edges: []
      });
    }
    const corridor = corridors.get(corridorIdx);
    corridor.children.add(child);
    corridor.edges.push(edge);
  }

  // Sort edges from lower to upper to minimize crossings
  for (let corridor of corridors.values()) {
    corridor.edges.sort(({ parent: pA }, { parent: pB }) => {
      if (pA.onlyTree === pB.onlyTree) {
        if (pA.onlyMaxDepth === pB.onlyMaxDepth) {
          return pA.order > pB.order ? -1 : 1;
        }
        return pA.onlyMaxDepth < pB.onlyMaxDepth ? -1 : 1;
      }
      return pA.onlyTree.order > pB.onlyTree.order ? -1 : 1;
    });
  }

  // Annotate edges with offset
  for (let corridor of corridors.values()) {
    const spacing = X_CORRIDOR_WIDTH / (corridor.edges.length + 1);
    for (let i=0; i<corridor.edges.length; ++i) {
      corridor.edges[i].offset = spacing * (i + 1);
    }
  }

  return corridors;
}

export function renderTrees(mTree, trees, svg, onNodeClick=NOOP) {
  const assignedMTree = new MultiTree(trees, true);

  const crossEdges = [];
  for (let [cNodeAui, mcNode] of mTree.crossNodes) {
    for (let parentAui of mcNode.input.keys()) {
      const cNode = assignedMTree.nodes.get(cNodeAui);
      const parent = assignedMTree.nodes.get(parentAui);
      if (cNode.onlyTree === parent.onlyTree) continue;
      crossEdges.push({ parent, child: cNode });
    }
  }

  // All cross edges and tree's deep edges must be determined at the same time
  // so corridors can be accurately caculated and paths do not overlap
  const deepEdges = [];
  for (let tree of trees.values()) {
    for (let [depth, nodes] of tree.nodesByMaxDepth) {
      if (depth > 0) {
        for (let node of nodes.values()) {
          for (let parent of node.input.values()) {
            // If the parent and child are separated by more than one depth
            // render the edges like internal cross edges
            if (Math.abs(parent.maxDepth - node.maxDepth) > 1) {
              deepEdges.push({ parent, child: node });
            }
          }
        }
      }
    }
  }

  // Render all cross and deep edges
  const corridors = buildCorridors([...crossEdges, ...deepEdges]);
  const corridorOffset = X_CORRIDOR_WIDTH / 2; // renderLongEdge's origin is center
  for (let corridor of corridors.values()) {
    for (let edge of corridor.edges) {
      const { parent, child, offset } = edge;
      renderLongEdge(parent, child, offset - corridorOffset, svg);
    }
  }

  // tree edges
  for (let tree of trees.values()) {
    // Assumes lines only go positive on x-axis from parent to child
    for (let [depth, nodes] of tree.nodesByMaxDepth) {
      if (depth > 0) {
        for (let node of nodes.values()) {
          for (let parent of node.input.values()) {
            if (Math.abs(parent.maxDepth - node.maxDepth) === 1) { // ignore deep edges
              const positionedParent = tree.nodesByMaxDepth.get(parent.maxDepth).get(parent.aui);
              renderEdge(positionedParent, node, svg);
            }
          }
        }
      }
    }
  }

  // tree nodes
  for (let tree of trees.values()) {
    const treeDataAttr = `[data-tree-id="${tree.id}"]`;
    for (let [depth, nodes] of tree.nodesByMaxDepth) {
      nodes = [...nodes.values()];
      svg.selectAll(`use.${styles.node}${treeDataAttr}[data-depth="${depth}"]`)
        .data(nodes)
        .enter().append('use')
          .attr('xlink:href', n => {
            let def = n.type;
            if (n.inferred) {
              def += '-inferred';
            }
            if (n.type === 'tap' || n.type === 'processor' || n.type === 'fork') {
              if (n.state === 'PAUSED') {
                def += '-paused';
              }
              if (n.state === 'STOPPED') {
                def += '-stopped';
              }
            }
            return '#' + def;
          }) // from <defs>
          .attr('class', n => cn({ [styles.clickable_node]: !n.inferred, [styles.inferred_node]: n.inferred }))
          .on('click', onNodeClick)
          .attr('data-depth', depth)
          .attr('data-aui', n => n.aui)
          .attr('x', n => n.x)
          .attr('y', n => n.y);
    }
  }

  // tree text
  for (let tree of trees.values()) {
    const treeDataAttr = `[data-tree-id="${tree.id}"]`;

    for (let [depth, nodes] of tree.nodesByMaxDepth) {
      nodes = [...nodes.values()]
        .map(n => {
          let displayName = n.displayName || n.aui?.split('/').slice(1).join('/');
          if (displayName.length > 39) {
            displayName = displayName.slice(0, 36) + '...';
          }
          return {
            ...n,
            displayName,
            x: n.x + 40,
            y: n.y + 12
          };
        });

      // Render same text with a stroke to give separation from background that
      // looks better than a flat white background
      svg.selectAll(`text.${styles.node_under}${treeDataAttr}[data-depth="${depth}"]`)
        .data(nodes).enter().append('text')
          .attr('class', styles.node_under)
          .attr('data-depth', depth)
          .attr('data-aui', n => n.aui)
          .attr('x', n => n.x)
          .attr('y', n => n.y)
          .text(n => n.displayName);

      svg.selectAll(`text.${styles.node}${treeDataAttr}[data-depth="${depth}"]`)
        .data(nodes).enter().append('text')
          .attr('class', styles.node)
          .attr('data-depth', depth)
          .attr('data-aui', n => n.aui)
          .attr('x', n => n.x)
          .attr('y', n => n.y)
          .text(n => n.displayName);
    }
  }
}
